<script>
import { userLogin } from '@/api/user.js';
import { generateCaptcha } from '@/api/captcha.js';
import constants from '@/constants';

export default {
    data() {
        return {
            // Form inputs
            userLogin: '',
            userPassword: '',
            userRemember: true,

            // Validation errors
            loginError: '',
            passwordError: '',
            authorizationError: '',

            // CAPTCHA state
            captchaRequired: false,
            captchaImage: '',
            captchaId: '',
            captchaInput: '',
            captchaError: '',

            // Rate-limit state (429 progressive delay)
            isRateLimited: false,
            rateLimitCountdown: 0,
            rateLimitTimer: null,
        };
    },
    methods: {
        async loadCaptcha() {
            const captchaResponse = await generateCaptcha();

            if (!captchaResponse.success || !captchaResponse.data) {
                this.captchaError =
                    captchaResponse.error || 'Failed to load CAPTCHA';
                return;
            }

            this.captchaImage = `data:image/jpeg;base64,${captchaResponse.data.image_data}`;
            this.captchaId = captchaResponse.data.challenge_id;
            this.captchaInput = '';
            this.captchaError = '';
        },

        validateForm() {
            this.authorizationError = '';
            this.captchaError = '';

            // Validate username
            this.loginError =
                !this.userLogin || this.userLogin.trim() === ''
                    ? 'Enter username'
                    : '';

            // Validate password
            this.passwordError =
                !this.userPassword || this.userPassword === ''
                    ? 'Enter password'
                    : '';

            // Validate CAPTCHA if required
            if (this.captchaRequired) {
                this.captchaError =
                    !this.captchaInput || this.captchaInput.trim() === ''
                        ? 'Enter CAPTCHA'
                        : '';
            }

            // Return true if validation passes
            return (
                this.loginError === '' &&
                this.passwordError === '' &&
                this.captchaError === ''
            );
        },

        handleCaptchaRequired(errorDetail) {
            this.captchaRequired = true;
            this.captchaImage = `data:image/jpeg;base64,${errorDetail.image_data}`;
            this.captchaId = errorDetail.challenge_id;
            this.captchaInput = '';
            this.authorizationError = 'Please complete the CAPTCHA below';
        },

        async handleLoginError(response) {
            // 429 – progressive delay
            if (response.rateLimited) {
                this.isRateLimited = true;
                this.rateLimitCountdown = response.retryAfterSeconds;
                this.authorizationError = response.errorText;
                this.startRateLimitCountdown();
                return;
            }

            const errorDetail = response.response?.data?.detail;

            if (
                typeof errorDetail === 'object' &&
                errorDetail?.error === 'CAPTCHA_REQUIRED'
            ) {
                this.handleCaptchaRequired(errorDetail);
                return;
            }

            // Other login error
            this.authorizationError = response.errorText;

            if (this.captchaRequired) {
                await this.loadCaptcha();
            }
        },

        clearCaptchaState() {
            this.captchaRequired = false;
            this.captchaImage = '';
            this.captchaId = '';
            this.captchaInput = '';
        },

        startRateLimitCountdown() {
            if (this.rateLimitTimer) clearInterval(this.rateLimitTimer);
            this.rateLimitTimer = setInterval(() => {
                this.rateLimitCountdown -= 1;
                this.authorizationError = `Too many failed login attempts. Please wait ${this.rateLimitCountdown} second${this.rateLimitCountdown !== 1 ? 's' : ''} before trying again.`;
                if (this.rateLimitCountdown <= 0) {
                    this.clearRateLimitState();
                }
            }, 1000);
        },

        clearRateLimitState() {
            if (this.rateLimitTimer) {
                clearInterval(this.rateLimitTimer);
                this.rateLimitTimer = null;
            }
            this.isRateLimited = false;
            this.rateLimitCountdown = 0;
        },

        async loadingValidate() {
            // Early return if validation fails
            if (!this.validateForm()) {
                return;
            }

            const response = await userLogin(
                this.userLogin,
                this.userPassword,
                this.captchaRequired ? this.captchaId : null,
                this.captchaRequired ? this.captchaInput : null
            );

            // Early return for failed authentication
            if (response.authorization === false) {
                await this.handleLoginError(response);
                return;
            }

            // Success - clear state and redirect
            this.clearCaptchaState();
            this.$router.push({ name: constants.ROUTER_NAME.MAIN });
        },
    },
    beforeUnmount() {
        if (this.rateLimitTimer) clearInterval(this.rateLimitTimer);
    },
};
</script>

<template>
    <!-- Header -->
    <div class="flex_column flex_header">
        <h2>AUTHORIZATION</h2>
        <hr />
    </div>

    <!-- Body -->
    <div class="flex_column flex_body">
        <!-- Login -->
        <label class="input_header"> LOGIN: </label>
        <input
            autocomplete="email"
            type="text"
            :class="
                loginError === ''
                    ? 'input_text input_text_default'
                    : 'input_text input_text_error'
            "
            placeholder="user login"
            v-model="userLogin"
        />
        <label class="red_color mt_5 ml_10">
            {{ loginError }}
        </label>

        <!-- Password -->
        <label class="input_header"> PASSWORD: </label>
        <input
            autocomplete="current-password"
            type="password"
            :class="
                passwordError === ''
                    ? 'input_text input_text_default'
                    : 'input_text input_text_error'
            "
            placeholder="user password"
            v-model="userPassword"
        />
        <label class="red_color mt_5 ml_10">
            {{ passwordError }}
        </label>

        <!-- CAPTCHA -->
        <div v-if="captchaRequired" class="captcha_section">
            <label class="input_header"> CAPTCHA: </label>
            <div class="captcha_image_container">
                <img :src="captchaImage" alt="CAPTCHA" class="captcha_image" />
                <button
                    type="button"
                    @click="loadCaptcha"
                    class="captcha_refresh_button cursor_pointer"
                    title="Refresh CAPTCHA"
                >
                    🔄
                </button>
            </div>
            <input
                type="text"
                :class="
                    captchaError === ''
                        ? 'input_text input_text_default captcha_input'
                        : 'input_text input_text_error captcha_input'
                "
                placeholder="Enter CAPTCHA"
                v-model="captchaInput"
                maxlength="10"
                autocomplete="off"
            />
            <label class="red_color mt_5 ml_10">
                {{ captchaError }}
            </label>
        </div>

        <!-- Remember -->
        <div class="mt_20">
            <input
                class="cursor_pointer"
                type="checkbox"
                id="remember"
                v-model="userRemember"
            />
            <label class="text_color cursor_pointer" for="remember">
                Remember Me
            </label>
        </div>

        <label class="red_color mt_20 align_center">
            {{ authorizationError }}
        </label>
    </div>

    <!-- Basement -->
    <div class="flex_column flex_basement">
        <hr />
        <button
            class="login_button cursor_pointer_label"
            @click="loadingValidate"
            :disabled="isRateLimited"
            type="button"
        >
            <b>LOGIN</b>
        </button>
    </div>
</template>

<style scoped>
/* Component */
hr {
    width: 100%;
    border: 1px solid var(--header-separator);
}
h2 {
    margin: 0px;
    margin-bottom: 10px;
    font-size: 20px;
    color: var(--header-text);
    text-align: center;
}

/* Color */
.text_color {
    color: var(--header-text);
}
.red_color {
    color: #fc424a;
}

/* Container-column */
.flex_column {
    display: flex;
    flex-direction: column;
}
.flex_header {
    margin-top: 10px;
    flex: 0 0 50px;
}
.flex_body {
    flex: 1 1 auto;
}
.flex_basement {
    margin-bottom: 10px;
    flex: 0 0 50px;
}

/* Input-text */
.input_text_default {
    border: 2px solid var(--header-separator);
}
.input_text_error {
    border: 2px solid #fc424a;
}
.input_text {
    background-color: var(--header);
    color: var(--header-text);

    border-radius: 10px;

    padding: 10px;

    font-size: 16px;
}
.input_text::placeholder {
    color: var(--header-text);
    padding-left: 5px;
}
.input_header {
    color: var(--header-text);
    margin-top: 20px;
    margin-bottom: 5px;
    margin-left: 10px;
    font-size: 12px;
}

/* Button */
.login_button {
    background-color: var(--left-button);
    color: var(--left-button-text);

    font-size: 20px;

    border: var(--border);
    border-radius: 10px;

    margin-top: 20px;
    margin-left: 10px;
    margin-right: 10px;
    padding: 10px;
}
.login_button:hover {
    background-color: var(--left-button-hover);
}

/* Label */
.align_center {
    text-align: center;
}

/* CAPTCHA */
.captcha_section {
    margin-top: 20px;
}

.captcha_image_container {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 5px;
    padding: 10px 15px;
    background-color: var(--header);
    border: 2px solid var(--header-separator);
    border-radius: 10px;
    gap: 12px;
}

.captcha_image {
    height: 40px;
    width: auto;
    border-radius: 4px;
    border: 1px solid var(--header-separator);
    background-color: white;
    padding: 3px;
}

.captcha_refresh_button {
    background-color: var(--left-button);
    color: var(--left-button-text);
    border: 1px solid var(--header-separator);
    border-radius: 6px;
    padding: 6px 8px;
    font-size: 14px;
    min-width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.2s ease;
}

.captcha_refresh_button:hover {
    background-color: var(--left-button-hover);
}

.captcha_input {
    text-align: center;
    letter-spacing: 1px;
    font-size: 16px;
}

.captcha_input::placeholder {
    text-transform: none;
    letter-spacing: normal;
}
</style>
